import React from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Layers, Music, Users, Video } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';

const PreparePage: React.FC = () => {
  return (
    <>
      <Navigation />
      <Container className="mt-4">
        <div className="text-center mb-5">
          <h1 className="display-4 mb-3">Prepare</h1>
          <p className="lead text-muted">
            Get ready for upcoming services and plan ahead
          </p>
        </div>

        <Row className="g-4 justify-content-center">
          <Col md={6} lg={3}>
            <Link to="/prepare/upcoming-services" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <Calendar size={48} className="text-primary" />
                  </div>
                  <Card.Title className="h4 mb-3">Upcoming Services</Card.Title>
                  <Card.Text className="flex-grow-1">
                    View upcoming worship services with songs, sheet music downloads, and practice videos.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-primary">View Services</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>

          <Col md={6} lg={3}>
            <Link to="/prepare/seasonal-planning" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <Layers size={48} className="text-purple" style={{ color: '#6B21A8' }} />
                  </div>
                  <Card.Title className="h4 mb-3">Season Planning</Card.Title>
                  <Card.Text className="flex-grow-1">
                    Big-picture planning for choir anthems by liturgical season. Brainstorm ideas for Advent, Lent, Easter, and more.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-secondary" style={{ borderColor: '#6B21A8', color: '#6B21A8' }}>Plan Seasons</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>

          <Col md={6} lg={3}>
            <Link to="/prepare/recordings" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <Video size={48} className="text-danger" />
                  </div>
                  <Card.Title className="h4 mb-3">Service Recordings</Card.Title>
                  <Card.Text className="flex-grow-1">
                    Watch the music from past Sunday services, one piece at a time.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-danger">Watch</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>

          <Col md={6} lg={3}>
            <Link to="/prepare/rehearsal-readings" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <BookOpen size={48} style={{ color: '#7A4E2D' }} />
                  </div>
                  <Card.Title className="h4 mb-3">Rehearsal Readings</Card.Title>
                  <Card.Text className="flex-grow-1">
                    Prepare Evening Prayer readings and BCP psalmody for choir rehearsals, then export a printable reading sheet.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-secondary" style={{ borderColor: '#7A4E2D', color: '#7A4E2D' }}>Open Readings</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>

          <Col md={6} lg={3}>
            <Link to="/prepare/preludes" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <Music size={48} style={{ color: '#1F5F5B' }} />
                  </div>
                  <Card.Title className="h4 mb-3">Preludes and Postludes</Card.Title>
                  <Card.Text className="flex-grow-1">
                    A reference list of composed pieces and hymn tunes to draw on. Not tied to any service.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-secondary" style={{ borderColor: '#1F5F5B', color: '#1F5F5B' }}>Open List</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>

          <Col md={6} lg={3}>
            <Link to="/prepare/brahms-requiem" className="text-decoration-none">
              <Card className="h-100 shadow-sm prepare-card">
                <Card.Body className="text-center d-flex flex-column">
                  <div className="prepare-card-icon mb-3">
                    <Users size={48} style={{ color: '#4f7a9a' }} />
                  </div>
                  <Card.Title className="h4 mb-3">Brahms Requiem</Card.Title>
                  <Card.Text className="flex-grow-1">
                    The orchestra seat by seat, with Brahms's lines played through it and passages tried on other instruments.
                  </Card.Text>
                  <div className="mt-auto">
                    <span className="btn btn-outline-secondary" style={{ borderColor: '#4f7a9a', color: '#4f7a9a' }}>Open the Plan</span>
                  </div>
                </Card.Body>
              </Card>
            </Link>
          </Col>
        </Row>

        <div className="mt-5 text-center">
          <h5 className="text-muted mb-3">Quick Tips</h5>
          <Row>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="text-primary mb-2">📄</div>
                <small className="text-muted">Click download icons to get sheet music</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="text-danger mb-2">▶️</div>
                <small className="text-muted">Click play icons for practice videos</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center mb-3">
                <div className="text-success mb-2">🎵</div>
                <small className="text-muted">All materials are updated weekly</small>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <style dangerouslySetInnerHTML={{
        __html: `
        .prepare-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid #e9ecef;
        }

        .prepare-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
        }

        .prepare-card-icon {
          opacity: 0.8;
        }

        .prepare-card:hover .prepare-card-icon {
          opacity: 1;
        }

        a.text-decoration-none:hover {
          text-decoration: none !important;
          color: inherit !important;
        }
        `
      }} />
    </>
  );
};

export default PreparePage;
